import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getMockDashboardData } from '../services/mockData';
import api from '../services/api'; // Import the configured API client

const USE_MOCK_DATA = false; // Set to false to use the real API

export const useDashboardData = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);


        // Check if we need to force a refresh (set by other components)
        const needsRefresh = localStorage.getItem('dashboardNeedsRefresh') === 'true';
        if (needsRefresh) {
          // Clear the flag after detecting it
          localStorage.removeItem('dashboardNeedsRefresh');
        }

        // Use mock data if specified
        if (USE_MOCK_DATA) {
          const mockData = getMockDashboardData();
          setDashboardData(mockData);
          setLoading(false);
          return;
        }

        // Base data structure
        const baseData = {
          user: {
            name: 'Autobahn GmbH',
            avatar: '/logos/UserLogo.png',
          },
          stats: {
            totalCustomers: {
              value: 1372902,
              change: -8,
              label: 'Asphaltmischgut bilanziert'
            },
            totalRevenue: {
              value: 0,
              change: -9,
              label: 'CO₂e Emissionen'
            },
            totalSales: {
              value: 0,
              change: -22,
              label: 'Einsparungen gegenüber 1990'
            }
          },
          // These will be populated from API
          topProducts: [],
          topCountries: [],
          topCustomers: [],
          recentOrders: []
        };

        // Fetch data from the API (in parallel)
        // Use no-cache endpoints if refresh is needed (needsRefresh was already declared above)
        const [
          largestProjectsResponse,
          largestSuppliersResponse,
          topMixturesResponse,
          totalNettoWeightResponse,
          totalCO2EmissionsResponse
        ] = await Promise.all([
          api.get(needsRefresh ? '/api/delivery-notes/largest-projects/no-cache' : '/api/delivery-notes/largest-projects'),
          api.get(needsRefresh ? '/api/delivery-notes/largest-suppliers/no-cache' : '/api/delivery-notes/largest-suppliers'),
          api.get(needsRefresh ? '/api/delivery-notes/top-mixtures/no-cache' : '/api/delivery-notes/top-mixtures'),
          api.get(needsRefresh ? '/api/delivery-notes/total-netto-weight/no-cache' : '/api/delivery-notes/total-netto-weight'),
          api.get(needsRefresh ? '/api/delivery-notes/total-co2-emissions/no-cache' : '/api/delivery-notes/total-co2-emissions')
        ]);

        // Update with real data from API responses
        if (largestProjectsResponse.data.success && largestProjectsResponse.data.data) {
          baseData.topCountries = largestProjectsResponse.data.data;
          // Use a subset for recentOrders if not populated separately
          baseData.recentOrders = largestProjectsResponse.data.data.slice(0, 3);
        }

        if (largestSuppliersResponse.data.success && largestSuppliersResponse.data.data) {
          baseData.topCustomers = largestSuppliersResponse.data.data;
        }

        if (topMixturesResponse.data.success && topMixturesResponse.data.data) {
          baseData.topProducts = topMixturesResponse.data.data;
        }

        // Update the first card with the total netto weight from all projects
        if (totalNettoWeightResponse.data.success && totalNettoWeightResponse.data.data !== undefined) {
          // Update the totalCustomers card with the actual total netto weight
          baseData.stats.totalCustomers.value = Math.round(totalNettoWeightResponse.data.data);
          baseData.stats.totalCustomers.label = 'Asphaltmischgut bilanziert';
          // Keep the change percentage as is
        }

        // Update CO₂ emissions and savings cards
        if (totalCO2EmissionsResponse.data.success && totalCO2EmissionsResponse.data.data) {
          const co2Data = totalCO2EmissionsResponse.data.data;

          // Update CO₂e Emissionen card (totalRevenue)
          if (co2Data.totalCurrentEmissions !== undefined) {
            baseData.stats.totalRevenue.value = co2Data.totalCurrentEmissions;
            baseData.stats.totalRevenue.label = 'CO₂e Emissionen';
          }

          // Update Einsparungen gegenüber 1990 card (totalSales)
          if (co2Data.totalSavings !== undefined) {
            baseData.stats.totalSales.value = co2Data.totalSavings;
            baseData.stats.totalSales.label = 'Einsparungen gegenüber 1990';
          }
        }

        setDashboardData(baseData);
      } catch (err) {
        console.error('Error fetching dashboard data:', {
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          url: err.config?.url
        });
        setError(err.message);

        // Fallback to mock data if API fails
        const mockData = getMockDashboardData();
        setDashboardData(mockData);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch data when on the dashboard page and we're authenticated
    const authToken = localStorage.getItem('authToken');
    if (location.pathname === '/dashboard' && authToken) {

      fetchData();
    } else if (location.pathname !== '/dashboard') {
      // For non-dashboard routes, set loading to false immediately
      // This prevents the global loading state from blocking other routes

      setLoading(false);
      setDashboardData(null);
      setError(null);
    }
  }, [location.pathname]);

  // Function to manually refresh dashboard data
  const refreshData = async () => {


    // Check if we're authenticated before proceeding
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      return;
    }

    setLoading(true);

    try {
      // Base data structure
      const baseData = {
        user: {
          name: 'Autobahn GmbH',
          avatar: '/logos/UserLogo.png',
        },
        stats: {
          totalCustomers: {
            value: 1372902,
            change: -8,
            label: 'Asphaltmischgut bilanziert'
          },
          totalRevenue: {
            value: 0,
            change: -9,
            label: 'CO₂e Emissionen'
          },
          totalSales: {
            value: 0,
            change: -22,
            label: 'Einsparungen gegenüber 1990'
          }
        },
        // These will be populated from API
        topProducts: [],
        topCountries: [],
        topCustomers: [],
        recentOrders: []
      };

      // Fetch data from the API (in parallel)
      // Always use no-cache endpoints for manual refresh
      const [
        largestProjectsResponse,
        largestSuppliersResponse,
        topMixturesResponse,
        totalNettoWeightResponse,
        totalCO2EmissionsResponse
      ] = await Promise.all([
        api.get('/api/delivery-notes/largest-projects/no-cache'),
        api.get('/api/delivery-notes/largest-suppliers/no-cache'),
        api.get('/api/delivery-notes/top-mixtures/no-cache'),
        api.get('/api/delivery-notes/total-netto-weight/no-cache'),
        api.get('/api/delivery-notes/total-co2-emissions/no-cache')
      ]);

      // Update with real data from API responses
      if (largestProjectsResponse.data.success && largestProjectsResponse.data.data) {
        baseData.topCountries = largestProjectsResponse.data.data;
        // Use a subset for recentOrders if not populated separately
        baseData.recentOrders = largestProjectsResponse.data.data.slice(0, 3);
      }

      if (largestSuppliersResponse.data.success && largestSuppliersResponse.data.data) {
        baseData.topCustomers = largestSuppliersResponse.data.data;
      }

      if (topMixturesResponse.data.success && topMixturesResponse.data.data) {
        baseData.topProducts = topMixturesResponse.data.data;
      }

      // Update the first card with the total netto weight from all projects
      if (totalNettoWeightResponse.data.success && totalNettoWeightResponse.data.data !== undefined) {
        // Update the totalCustomers card with the actual total netto weight
        baseData.stats.totalCustomers.value = Math.round(totalNettoWeightResponse.data.data);
        baseData.stats.totalCustomers.label = 'Asphaltmischgut bilanziert';
        // Keep the change percentage as is
      }

      // Update CO₂ emissions and savings cards
      if (totalCO2EmissionsResponse.data.success && totalCO2EmissionsResponse.data.data) {
        const co2Data = totalCO2EmissionsResponse.data.data;

        // Update CO₂e Emissionen card (totalRevenue)
        if (co2Data.totalCurrentEmissions !== undefined) {
          baseData.stats.totalRevenue.value = co2Data.totalCurrentEmissions;
          baseData.stats.totalRevenue.label = 'CO₂e Emissionen';
        }

        // Update Einsparungen gegenüber 1990 card (totalSales)
        if (co2Data.totalSavings !== undefined) {
          baseData.stats.totalSales.value = co2Data.totalSavings;
          baseData.stats.totalSales.label = 'Einsparungen gegenüber 1990';
        }
      }

      setDashboardData(baseData);
    } catch (err) {
      console.error('Error refreshing dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return { dashboardData, loading, error, refreshData };
};
